import {reactFetchJSON} from '@github-ui/verified-fetch'
import {useInvalidateOnAliveUpdate} from '@github-ui/use-invalidate-on-alive-update'
import {skipToken, useQuery, useQueryClient} from '@github-ui/react-query'
import {AuthSessionExpiredError} from '@github-ui/pull-request-page-data-tooling/fetch-error-handling'

import type {PullRequestAliveData} from '../../../hooks/use-refetch-on-alive-update'
import {useRefetchOnAliveUpdate} from '../../../hooks/use-refetch-on-alive-update'
import type {ProofOfPresenceRoutePayload} from '../../../routes/route-payload-types'
import {useOverviewRouteData} from './use-overview-route-data'

const pullRequestFilter = (data: PullRequestAliveData) => Boolean(data.event_updates?.git_updated)

export function useProofOfPresence() {
  const overviewQuery = useOverviewRouteData()
  const proofOfPresenceUrl = overviewQuery.data?.proofOfPresenceData?.url ?? null
  const queryKey = proofOfPresenceUrl ? ['proof-of-presence', proofOfPresenceUrl] : ['proof-of-presence', 'disabled']
  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey,
    enabled: proofOfPresenceUrl != null,
    staleTime: 0,
    queryFn:
      proofOfPresenceUrl == null
        ? skipToken
        : async ({signal}) => {
            const response = await reactFetchJSON(proofOfPresenceUrl, {signal})
            if (response.status === 401) {
              const previousData = queryClient.getQueryData<ProofOfPresenceRoutePayload>(queryKey)
              if (previousData) return previousData
              throw new AuthSessionExpiredError()
            }
            if (!response.ok) throw new Error(`HTTP ${response.status}`)
            return (await response.json()) as ProofOfPresenceRoutePayload
          },
  })

  const channels = query.data?.aliveChannels

  useRefetchOnAliveUpdate(channels?.proofOfPresence, query.refetch)
  useRefetchOnAliveUpdate(channels?.headRef, query.refetch)
  useRefetchOnAliveUpdate(channels?.baseRef, query.refetch)
  useRefetchOnAliveUpdate(channels?.gitMergeState, query.refetch)
  useInvalidateOnAliveUpdate(channels?.pullRequest ? channels.pullRequest : null, queryKey, {
    filter: pullRequestFilter,
  })

  const isError = query.isError && proofOfPresenceUrl != null
  return {
    proofOfPresence: query.data?.presentation ?? null,
    refetch: query.refetch,
    isFetching: query.isFetching,
    isError,
  }
}
